import React, {useCallback} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {GiftshopPackageData, IconType} from 'types'
import {getCurrencyValue} from 'utils'
import {Icon, Image, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {ThemeColor} from 'themes'
import {useSelector} from 'lib/redux'

interface StyledPictureProps {
  padded?: boolean
}

const StyledPicture = styled(Image)<StyledPictureProps>`
  object-fit: contain;
  height: ${convertUnit(48)};
  border-radius: ${convertUnit(2.5)};
  aspect-ratio: 0.75;
  border: 1px solid ${({theme}) => theme.white_1};
  margin-top: ${({padded}) => (padded ? convertUnit(10) : 0)};
`

const StyledPackageContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(18)};
  padding: ${convertUnit(9)} ${convertUnit(20)};
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
`

const StyledPackagePictureContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledPackageDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledPackageActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: ${convertUnit(4)};
`

const StyledPackageAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export default function GiftShopSettingPackageContentItem({
  package: packageData,
  onClickPackage,
  onClickShare,
}: {
  package: GiftshopPackageData
  onClickPackage: () => void
  onClickShare?: () => void
}) {
  const {translate} = useTranslation()
  const user = useSelector('user')!!

  const {duration, price, tree_name, tree_photo_url} = packageData

  const handleRenderPackageAction = useCallback(
    (icon: IconType, color: ThemeColor, text: string, onClick?: () => void) => (
      <StyledPackageAction
        onClick={(e) => {
          if (onClick) {
            e.stopPropagation()
            onClick()
          }
        }}>
        <Icon size={12} color={color} type={icon} />
        <Paragraph color={color}>{text}</Paragraph>
      </StyledPackageAction>
    ),
    [],
  )

  return (
    <StyledPackageContainer onClick={onClickPackage}>
      <StyledPackagePictureContainer>
        <StyledPicture src={user.photo} alt="Creator Photo" />
        <StyledPicture padded src={tree_photo_url} alt="Tree Photo" />
      </StyledPackagePictureContainer>
      <StyledPackageDetailContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {tree_name}
        </Paragraph>
        <Paragraph color="gray_5">{getCurrencyValue(price)}</Paragraph>
        <StyledPackageActionContainer>
          {handleRenderPackageAction(
            'clock-ol',
            'warning_5',
            translate('global:days', {
              day: Math.round(duration / 24),
            }),
          )}
          {onClickShare && (
            <>
              <Paragraph color="gray_5">•</Paragraph>
              {handleRenderPackageAction(
                'share',
                'primary_5',
                translate('global:share'),
                onClickShare,
              )}
            </>
          )}
        </StyledPackageActionContainer>
      </StyledPackageDetailContainer>
    </StyledPackageContainer>
  )
}
